import config from '../config';
import axios from 'axios';

/**
 * Strapi-based URL Shortener Service
 * Uses Strapi content types for URL management
 */

/**
 * Generates a random short code for URL shortening
 * @param {number} length - Length of the short code (default: 8)
 * @returns {string} - Random alphanumeric string
 */
function generateShortCode(length = 8) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Creates a shortened URL using Strapi API
 * @param {string} originalUrl - The long URL to shorten
 * @param {string} strapiJwt - Strapi JWT token for authentication
 * @param {number} expirationHours - Hours until URL expires (default: 168 = 7 days)
 * @param {string} customCode - Optional custom short code
 * @returns {Promise<Object>} - Object containing shortUrl and shortCode
 */
export async function createShortUrl(originalUrl, strapiJwt, expirationHours = 168, customCode = null) {
  try {
    const shortCode = customCode || generateShortCode();
    const expiresAt = new Date(Date.now() + (expirationHours * 60 * 60 * 1000)).toISOString();
    
    const response = await axios.post(`${config.apiUrl}/api/short-urls`, {
      data: {
        shortCode,
        originalUrl,
        expiresAt,
        clickCount: 0
      }
    }, {
      headers: {
        'Authorization': `Bearer ${strapiJwt}`,
        'Content-Type': 'application/json'
      }
    });

    const data = response.data.data;
    const shortUrl = `${window.location.origin}/${data.attributes.shortCode}`;
    
    return {
      shortUrl,
      shortCode: data.attributes.shortCode,
      originalUrl: data.attributes.originalUrl,
      expiresAt: data.attributes.expiresAt,
      id: data.id
    };
  } catch (error) {
    if (error.response?.status === 400 && error.response?.data?.error?.message?.includes('shortCode')) {
      // Short code already exists, try again with a new one
      return createShortUrl(originalUrl, strapiJwt, expirationHours);
    }
    console.error('Error creating short URL:', error);
    throw new Error('Failed to create short URL: ' + (error.response?.data?.error?.message || error.message));
  }
}

/**
 * Resolves a short code to get the original URL using Strapi public API
 * @param {string} shortCode - The short code to resolve
 * @returns {Promise<Object>} - Object containing original URL and metadata
 */
export async function resolveShortUrl(shortCode) {
  try {
    const response = await axios.get(`${config.apiUrl}/api/short-urls`, {
      params: {
        'filters[shortCode][$eq]': shortCode,
        'populate': '*'
      }
    });

    const shortUrls = response.data.data;
    
    if (!shortUrls || shortUrls.length === 0) {
      throw new Error('Short URL not found');
    }

    const shortUrlData = shortUrls[0];
    const expiresAt = new Date(shortUrlData.attributes.expiresAt);
    
    // Check if expired
    //if (Date.now() > expiresAt.getTime()) {
    //  throw new Error('Short URL has expired');
    //}

    // Optional: Increment click count
    try {
      await axios.put(`${config.apiUrl}/api/short-urls/${shortUrlData.id}`, {
        data: {
          clickCount: (shortUrlData.attributes.clickCount || 0) + 1
        }
      });
    } catch (error) {
      console.warn('Failed to update click count:', error);
      // Don't throw error for analytics failure
    }

    return {
      originalUrl: shortUrlData.attributes.originalUrl,
      shortCode: shortUrlData.attributes.shortCode,
      expiresAt: shortUrlData.attributes.expiresAt,
      clickCount: shortUrlData.attributes.clickCount
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('Short URL not found');
      }
    }
    console.error('Error resolving short URL:', error);
    throw error;
  }
}

/**
 * Generates a shortened URL for share links (integrates with existing shareUtils)
 * @param {string} originalUrl - The original long share URL
 * @param {string} strapiJwt - Strapi JWT token for authentication
 * @param {number} expirationHours - Hours until URL expires
 * @returns {Promise<string>} - The shortened URL
 */
export async function createShortShareUrl(originalUrl, strapiJwt, expirationHours = 168) {
  try {
    const result = await createShortUrl(originalUrl, strapiJwt, expirationHours);
    return result.shortUrl;
  } catch (error) {
    console.error('Error creating short share URL:', error);
    // Fallback to original URL if shortening fails
    return originalUrl;
  }
}

/**
 * Client-side fallback URL shortener (stores in localStorage)
 * Use only when backend is not available - data won't persist across devices
 * @param {string} originalUrl - The long URL to shorten
 * @param {number} expirationHours - Hours until URL expires
 * @returns {string} - The shortened URL
 */
export function createShortUrlFallback(originalUrl, expirationHours = 168) {
  const shortCode = generateShortCode();
  const expiresAt = Date.now() + (expirationHours * 60 * 60 * 1000);
  
  // Store in localStorage
  const urlData = {
    originalUrl,
    expiresAt,
    createdAt: Date.now()
  };
  
  localStorage.setItem(`short_url_${shortCode}`, JSON.stringify(urlData));
  
  return `${window.location.origin}/${shortCode}`;
}

/**
 * Resolves a locally stored short URL (fallback method)
 * @param {string} shortCode - The short code to resolve
 * @returns {string|null} - The original URL or null if not found/expired
 */
export function resolveShortUrlFallback(shortCode) {
  try {
    const stored = localStorage.getItem(`short_url_${shortCode}`);
    if (!stored) return null;
    
    const urlData = JSON.parse(stored);
    
    // Check if expired
    if (Date.now() > urlData.expiresAt) {
      localStorage.removeItem(`short_url_${shortCode}`);
      return null;
    }
    
    return urlData.originalUrl;
  } catch (error) {
    console.error('Error resolving fallback short URL:', error);
    return null;
  }
}

/**
 * Deletes a short URL record from Strapi
 * @param {string} shortUrlId - The short URL record ID to delete
 * @param {string} strapiJwt - Strapi JWT token for authentication
 * @returns {Promise<void>}
 */
export async function deleteShortUrl(shortUrlId, strapiJwt) {
  try {
    const response = await axios.delete(`${config.apiUrl}/api/short-urls/${shortUrlId}`, {
      headers: {
        'Authorization': `Bearer ${strapiJwt}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status !== 200 && response.status !== 204) {
      throw new Error(`Failed to delete short URL: ${response.status}`);
    }
  } catch (error) {
    console.error('Error deleting short URL:', error);
    throw new Error('Failed to delete short URL: ' + (error.response?.data?.error?.message || error.message));
  }
}

/**
 * Creates a shortened URL with automatic fallback
 * High-level function for use in dialogs and components
 * @param {string} originalUrl - The URL to shorten
 * @param {string} strapiJwt - Strapi JWT token for authentication
 * @param {number} expirationHours - Hours until expiration
 * @param {boolean} useFallback - Whether to use localStorage fallback on error
 * @returns {Promise<Object>} - Object with shortUrl, success, and error info
 */
export async function shortenUrl(originalUrl, strapiJwt, expirationHours = 168, useFallback = true) {
  try {
    // Try Strapi URL shortening first
    const result = await createShortUrl(originalUrl, strapiJwt, expirationHours);
    return {
      shortUrl: result.shortUrl,
      shortCode: result.shortCode,
      success: true,
      method: 'strapi',
      expiresAt: result.expiresAt
    };
  } catch (error) {
    console.warn('Strapi URL shortening failed:', error);
    
    if (useFallback) {
      try {
        // Fall back to client-side shortening
        const fallbackUrl = createShortUrlFallback(originalUrl, expirationHours);
        const shortCode = fallbackUrl.split('/').pop();
        
        return {
          shortUrl: fallbackUrl,
          shortCode,
          success: true,
          method: 'fallback',
          warning: 'Using local storage fallback - URL may not work on other devices'
        };
      } catch (fallbackError) {
        console.error('Fallback URL shortening also failed:', fallbackError);
        return {
          shortUrl: originalUrl,
          success: false,
          method: 'none',
          error: 'URL shortening failed, using original URL'
        };
      }
    }
    
    return {
      shortUrl: originalUrl,
      success: false,
      method: 'none',
      error: error.message || 'URL shortening failed'
    };
  }
}

